import { useEffect, startTransition } from 'react';
import PageSeo from '@framework/seo/PageSeo';
import { useRouter, getQueryString, removeQueryParams } from '@framework/router';
import { getIssuerRoute } from '@/creditflowresearch/Router/RouteWithRedirect/getNewRoute';
import MissingSomethingModalButton from '@/creditflowresearch/ui/common/components/MissingSomethingModalButton';
import ResearchTile from '@/creditflowresearch/ui/components/research/components/ResearchTile';
import MetaSchema from '@/creditflowresearch/ui/common/components/MetaSchema';
import getIssuerMetaSchema from '@/creditflowresearch/ui/components/issuer/getIssuerMetaSchema';
import { BondsTileWithFallback } from '@/creditflowresearch/ui/components/bonds/BondsTile';
import { TraceTileWithFallback } from '@/creditflowresearch/ui/components/trace/TraceTile';
import { usePageFiltersContext } from '@/creditflowresearch/application/PageFilters/PageFiltersContext';
import { ArticlesTilesWithFallback } from '@/creditflowresearch/ui/components/creditFlow/articles/ArticlesTiles';
import getIssuerBreadcrumbs from './getIssuerBreadcrumbs';
import { useBreadcrumbs, useMounted } from '@/creditflowresearch/ui/common/hooks';
import DealByProductTile from '@/creditflowresearch/ui/components/deals/DealTiles/DealByProductTile';
import DealTileWithFallback from '@/creditflowresearch/ui/components/deals/DealTiles/DealTileWithFallback';
import { useUserPlanContext } from '@/creditflowresearch/application/User/UserPlan/UserPlanContext';
import { UserPlanType } from '@creditflowresearch/user-plan/UserPlan';
import ProductPageHeader from '@/creditflowresearch/ui/common/components/templates/ProductPageHeader';
import { useProductGradient } from '@/creditflowresearch/ui/common/hooks/products/useProductGradient';
import { RoadshowsTileWithFallback } from '@/creditflowresearch/ui/components/Roadshows/Tile';
import { useParentPageContext } from '@/creditflowresearch/application/ParentPageContext';
import { useAccessGrant } from '@/creditflowresearch/application/User/access/grants/useAccessGrant';

const Issuer = () => {
  const { query, asPath, replace } = useRouter();
  const isMounted = useMounted();
  const {
    company,
    checkAccessByUserRegions,
    checkAccessByUserProducts,
    regionFilters,
    productFilters,
  } = usePageFiltersContext();
  const companyName = company?.name || ''; // if we have name - it indicates that company is loaded
  const search = removeQueryParams(query, ['companyViewId', 'issuerName']);
  const { products, regions } = query;
  const panelBackground = useProductGradient();

  const { checkAccessByUserPlan } = useUserPlanContext();
  const granted = useAccessGrant();
  // TODO: remove when the European market opening period ends
  const userAccessChecks = [
    granted || checkAccessByUserRegions(),
    granted || checkAccessByUserProducts(),
  ];

  const { parent } = useParentPageContext();

  useBreadcrumbs(
    () => companyName ? getIssuerBreadcrumbs({ products, regions }, parent, company) : null,
    [companyName, products, regions],
  );

  useEffect(() => {
    if (!isMounted || !companyName) {
      return;
    }

    const correctRoute = getIssuerRoute(company);
    const pathname = asPath.split('?')[0];

    if (correctRoute !== pathname) {
      const newSearchQuery = getQueryString(search);
      startTransition(() => {
        replace(`${ correctRoute }?${ newSearchQuery }`, undefined, { shallow: true });
      });
    }
  }, [company, isMounted, companyName, asPath, search, replace]);

  return (
    <>
      { companyName && (
        <>
          <PageSeo title={ companyName } />
          <MetaSchema schema={ getIssuerMetaSchema(getIssuerRoute(company), companyName) } />
        </>
      ) }
      <ProductPageHeader
        title={ companyName }
        withoutProductTitle
        items={ [
          { key: 'regions', items: regionFilters },
          { key: 'products', items: productFilters },
        ] }
        // TODO: remove when the European market opening period ends
        accessConditions={ [{ checks: [granted || checkAccessByUserPlan(UserPlanType.STARTER)] }] }
        // TODO: remove when the European market opening period ends
        blockingAccessConditions={ [{ checks: [granted || checkAccessByUserPlan(UserPlanType.STARTER)] }] }
        panelBackground={ panelBackground }
      />
      <ArticlesTilesWithFallback userAccessChecks={ userAccessChecks } />
      <RoadshowsTileWithFallback userAccessChecks={ userAccessChecks } />
      <DealTileWithFallback userAccessChecks={ userAccessChecks }>
        <DealByProductTile title="Deals" />
      </DealTileWithFallback>
      <BondsTileWithFallback userAccessChecks={ userAccessChecks } />
      <TraceTileWithFallback userAccessChecks={ userAccessChecks } />
      <ResearchTile />
      <MissingSomethingModalButton />
    </>
  );
};

export default Issuer;
